import { truncate } from "lodash";
import React, { useState } from "react";
import toast from "react-hot-toast";
import Select from "react-select";
import imageCompression from "browser-image-compression";
import Loader from "../../../common/Loader";
import { formattedCountries } from "../../../helpers/data";
import InputField from "../../../assets/components/InputField";
import DropDownField from "../../../assets/components/DropDownField";
import TextAreaField from "../../../assets/components/TextAreaField";
import PrimaryButton from "../../../assets/components/PrimaryButton";
import SecondaryButton from "../../../assets/components/SecondaryButton";

// Styles to make the searchable react-select controls match the InputField look.
const selectStyles = {
    control: (base) => ({
        ...base,
        backgroundColor: "#ffffff",
        borderColor: "#E5E7EB",
        borderRadius: "8px",
        minHeight: "48px",
        width: "100%",
        boxSizing: "border-box",
        boxShadow: "none",
    }),
};

const FormTwo = ({ formData, setFormData, handleBack, isSigningUp }) => {
    const [uploadingImg, setUploadingImg] = useState(false);

    const handleImageUpload = async (event) => {
        setUploadingImg(true);
        let file = event?.target?.files?.[0];

        // Check if file size is greater than 1MB before compression
        if (file && file.size > 1024 * 1024) {
            const options = {
                maxSizeMB: 1, // Target max size in MB
                maxWidthOrHeight: 1200, // Keep quality but limit dimensions
                useWebWorker: true,
            };

            file = await imageCompression(file, options);
        }

        if (file && file.size > 1024 * 1024) {
            toast.error("File size must be less than 1MB");
            event.target.value = "";
            return;
        }

        setFormData((prev) => ({
            ...prev,
            logo: file,
        }));
        setUploadingImg(false);
    };

    return (
        <div className="w-full max-w-[720px]">
            <div className="grid grid-cols-2 gap-x-6 max-sm:grid-cols-1">
                <InputField
                    label="Business Name"
                    required
                    type="text"
                    name="name"
                    placeholder="Enter your business name"
                    value={formData.name}
                    onChange={(e) => {
                        setFormData((prev) => ({
                            ...prev,
                            name: e.target.value,
                        }));
                    }}
                />

                <DropDownField
                    label="Type of Business"
                    required
                    name="type"
                    placeholder="Type of Business"
                    value={formData.type}
                    options={[
                        { label: "Sole Proprietorship", value: "sole proprietorship" },
                        { label: "Partnership", value: "partnership" },
                        { label: "Corporation", value: "corporation" },
                        {
                            label: "Limited Liability Company",
                            value: "limited liability company",
                        },
                    ]}
                    onChange={(e) => {
                        setFormData((prev) => ({
                            ...prev,
                            type: e.target.value,
                        }));
                    }}
                />
            </div>

            <InputField
                label="Business Email"
                required
                type="email"
                name="business_email"
                placeholder="b...cd@xyz.com"
                pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                value={formData.email}
                onChange={(e) => {
                    setFormData((prev) => ({
                        ...prev,
                        email: e.target.value,
                    }));
                }}
            />

            <InputField
                label="Business Phone Number"
                required
                type="number"
                name="business_phone_number"
                placeholder="+1 123 456 7890"
                minLength={10}
                pattern="^[0-9]+$"
                title="must be a valid phone number"
                value={formData.phone_number}
                onChange={(e) => {
                    setFormData((prev) => ({
                        ...prev,
                        phone_number: e.target.value,
                    }));
                }}
            />

            <InputField
                label="Business Street Address"
                required
                type="text"
                name="street"
                placeholder="Enter your street address"
                value={formData.street}
                onChange={(e) => {
                    setFormData((prev) => ({
                        ...prev,
                        street: e.target.value,
                    }));
                }}
            />

            <div className="grid grid-cols-3 gap-x-6 max-sm:grid-cols-1">
                <InputField
                    label="City"
                    required
                    type="text"
                    name="city"
                    placeholder="City"
                    value={formData.city}
                    onChange={(e) => {
                        setFormData((prev) => ({
                            ...prev,
                            city: e.target.value,
                        }));
                    }}
                />

                <InputField
                    label="Postal Code"
                    required
                    type="text"
                    name="postal_code"
                    placeholder="Postal Code"
                    value={formData.postal_code}
                    onChange={(e) => {
                        setFormData((prev) => ({
                            ...prev,
                            postal_code: e.target.value,
                        }));
                    }}
                />

                <div className="w-full font-body mb-4">
                    <label className="block font-body text-base mb-2">Country*</label>
                    <Select
                        name="country"
                        options={formattedCountries}
                        onChange={(e) => {
                            setFormData((prev) => ({
                                ...prev,
                                country: e.value,
                            }));
                        }}
                        placeholder="Country"
                        styles={selectStyles}
                        className="w-full"
                        required
                    />
                </div>
            </div>

            <div className="w-full font-body mb-4">
                <label className="block font-body text-base mb-2">
                    Upload Logo (.jpg and .png supported, max file size 1MB)
                </label>
                <div className="flex relative">
                    <input
                        type={"text"}
                        className="w-full bg-white text-txt-main px-4 py-3 rounded-noq-sm border border-border-color text-base shadow-sm !pr-44"
                        defaultValue={
                            formData?.logo?.name
                                ? truncate(formData?.logo?.name, { length: 30 })
                                : ""
                        }
                        required
                        disabled
                    />
                    <button
                        type="button"
                        className="absolute right-[1px] top-[1px] h-[46px] text-base text-txt-main bg-gray-200 px-6 rounded-noq-sm cursor-pointer"
                    >
                        {uploadingImg ? <Loader size={4} /> : "Upload..."}
                    </button>
                    <input
                        type={"file"}
                        accept="image/*"
                        className="absolute w-full h-full opacity-0 cursor-pointer"
                        disabled={uploadingImg}
                        onChange={(event) => {
                            handleImageUpload(event);
                        }}
                    />
                </div>
            </div>

            <TextAreaField
                label="Services"
                required
                name="services"
                rows={3}
                placeholder="List the services you offer"
                value={formData.services}
                onChange={(e) => {
                    setFormData((prev) => ({
                        ...prev,
                        services: e.target.value,
                    }));
                }}
            />

            <div className="flex gap-6 mt-6 max-sm:flex-col-reverse">
                <SecondaryButton onClick={handleBack} className="flex-1 max-sm:w-full">
                    &lt; Back
                </SecondaryButton>
                <PrimaryButton type="submit" className="flex-1 max-sm:w-full">
                    {isSigningUp ? "Registering..." : "Finish"}
                </PrimaryButton>
            </div>
        </div>
    );
};

export default FormTwo;
